import { useEffect, useRef, useState, useCallback } from "react";
import * as Sentry from "@sentry/browser";
import type { PlayerStatus, PlayerHookReturn } from "../types";
import { diagnosticEventLog } from "../../../shared/services/diagnostic";
import {
  buildPlayerFailureTags,
  getPlayerFailureDiagnostics,
} from "../playerNetworkDiagnostics";

const SHOUTCAST_AUTH_TOKEN = "SentanaECB";
export const MAX_SHOUTCAST_RETRIES = 5;
const MAX_RETRIES = MAX_SHOUTCAST_RETRIES;
const BASE_RETRY_DELAY_MS = 1500;
const MAX_RETRY_DELAY_MS = 30000;
const LIVENESS_TIMEOUT_MS = 10000; // No timeupdate for 10s = silent drop
const DRIFT_CHECK_INTERVAL_MS = 10_000; // Check drift every 10s
const DRIFT_THRESHOLD_S = 15; // Reconnect if >15s behind live edge
const DRIFT_RECONNECT_COOLDOWN_MS = 60_000; // Max one drift reconnect per minute

interface UseShoutcastPlayerOptions {
  audioElement: HTMLAudioElement | null;
  address: string | null;
  enabled: boolean;
  authenticated: boolean;
}

function getRetryDelay(attempt: number): number {
  const exponential = BASE_RETRY_DELAY_MS * Math.pow(2, attempt);
  const capped = Math.min(exponential, MAX_RETRY_DELAY_MS);
  return Math.round(capped * (0.5 + Math.random() * 0.5));
}

function buildShoutcastUrl(address: string): string {
  return `${address}/shoutcast?token=${SHOUTCAST_AUTH_TOKEN}`;
}

/**
 * React hook for managing Shoutcast HTTP audio streaming.
 *
 * Uses a plain <audio> element with an HTTP stream URL. No third-party library.
 * Background-safe: Chrome doesn't throttle <audio> elements in background tabs.
 *
 * Includes liveness monitoring — if no timeupdate fires for 10s while connected,
 * treats it as a silent drop and triggers reconnection.
 */
export const useShoutcastPlayer = ({
  audioElement,
  address,
  enabled,
  authenticated,
}: UseShoutcastPlayerOptions): PlayerHookReturn => {
  const [status, setStatus] = useState<PlayerStatus>("idle");
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const connectionIdRef = useRef(0);
  const retryTimeoutRef = useRef<number | null>(null);
  const livenessIntervalRef = useRef<number | null>(null);
  const lastTimeupdateRef = useRef(0);
  const currentAddressRef = useRef<string | null>(null);
  const driftIntervalRef = useRef<number | null>(null);
  const lastDriftReconnectRef = useRef(0);
  const startLivenessMonitorRef = useRef<(id: number) => void>(() => {});
  const startDriftMonitorRef = useRef<(id: number) => void>(() => {});

  const setVolume = useCallback(
    (volume: number) => {
      if (audioElement) {
        audioElement.volume = volume;
      }
    },
    [audioElement]
  );

  const isStaleConnection = useCallback((connectionId: number): boolean => {
    return connectionIdRef.current !== connectionId;
  }, []);

  // Start liveness monitor — checks that timeupdate events keep firing
  const startLivenessMonitor = useCallback(
    (connectionId: number) => {
      if (livenessIntervalRef.current) {
        clearInterval(livenessIntervalRef.current);
      }
      lastTimeupdateRef.current = Date.now();
      livenessIntervalRef.current = setInterval(() => {
        if (isStaleConnection(connectionId)) {
          clearInterval(livenessIntervalRef.current!);
          livenessIntervalRef.current = null;
          return;
        }
        const elapsed = Date.now() - lastTimeupdateRef.current;
        if (elapsed > LIVENESS_TIMEOUT_MS) {
          console.warn(
            `[Shoutcast #${connectionId}] No timeupdate for ${(elapsed / 1000).toFixed(1)}s — silent drop detected`
          );
          diagnosticEventLog.record("shoutcast:audio", "warn", "liveness-timeout", {
            connectionId,
            elapsedMs: elapsed,
          });
          clearInterval(livenessIntervalRef.current!);
          livenessIntervalRef.current = null;
          setStatus("error");
          setError(new Error("Shoutcast stream silent — reconnecting"));
        }
      }, 3000) as unknown as number;
    },
    [isStaleConnection]
  );

  const stopLivenessMonitor = useCallback(() => {
    if (livenessIntervalRef.current) {
      clearInterval(livenessIntervalRef.current);
      livenessIntervalRef.current = null;
    }
  }, []);

  // Stop drift monitor
  const stopDriftMonitor = useCallback(() => {
    if (driftIntervalRef.current) {
      clearInterval(driftIntervalRef.current);
      driftIntervalRef.current = null;
    }
  }, []);

  // Perform silent reconnect — keeps status as "connected" to avoid TTS announcements
  const performSilentReconnect = useCallback(
    (driftSeconds: number) => {
      if (!audioElement || !currentAddressRef.current) return;

      stopLivenessMonitor();
      stopDriftMonitor();
      lastDriftReconnectRef.current = Date.now();

      connectionIdRef.current++;
      const newConnectionId = connectionIdRef.current;
      const shoutcastUrl = buildShoutcastUrl(currentAddressRef.current);

      diagnosticEventLog.record("shoutcast:audio", "warn", "drift-reconnect", {
        connectionId: newConnectionId,
        driftSeconds,
      });
      Sentry.addBreadcrumb({
        category: "audio",
        message: `Shoutcast drift reconnect (${driftSeconds.toFixed(1)}s drift)`,
        level: "warning",
        data: { connectionId: newConnectionId, driftSeconds },
      });
      console.log(
        `[Shoutcast #${newConnectionId}] Drift reconnect — ${driftSeconds.toFixed(1)}s behind live edge`
      );

      // Event handlers for the reconnected stream
      const onPlaying = () => {
        if (isStaleConnection(newConnectionId)) return;
        diagnosticEventLog.record("shoutcast:audio", "info", "drift-reconnect-success", {
          connectionId: newConnectionId,
        });
        Sentry.addBreadcrumb({
          category: "audio",
          message: "Shoutcast drift reconnect success",
          level: "info",
          data: { connectionId: newConnectionId },
        });
        console.log(`[Shoutcast #${newConnectionId}] Drift reconnect successful`);
        // Do NOT call setStatus — stays "connected" to suppress TTS
        startLivenessMonitorRef.current(newConnectionId);
        startDriftMonitorRef.current(newConnectionId);
      };

      const onTimeupdate = () => {
        if (isStaleConnection(newConnectionId)) return;
        lastTimeupdateRef.current = Date.now();
      };

      const onError = () => {
        if (isStaleConnection(newConnectionId)) return;
        const mediaError = audioElement.error;
        const errMsg = mediaError
          ? `Shoutcast drift reconnect MediaError code ${mediaError.code}: ${mediaError.message || "no message"}`
          : "Shoutcast drift reconnect failed";
        diagnosticEventLog.record("shoutcast:audio", "error", "drift-reconnect-failed", {
          connectionId: newConnectionId,
          errorCode: mediaError?.code,
          errorMessage: mediaError?.message,
          address: currentAddressRef.current,
          driftSeconds,
        });
        Sentry.captureException(new Error(errMsg), {
          tags: {
            component: "useShoutcastPlayer",
            context: "shoutcast_drift_reconnect_failed",
            connection_type: "shoutcast",
          },
          extra: {
            connectionId: newConnectionId,
            address: currentAddressRef.current,
            driftSeconds,
          },
          fingerprint: ["useShoutcastPlayer", "drift-reconnect-failed"],
        });
        console.error(`[Shoutcast #${newConnectionId}] Drift reconnect failed:`, errMsg);
        // Fall through to normal error/retry path
        setStatus("error");
        setError(new Error(errMsg));
      };

      const onEnded = () => {
        if (isStaleConnection(newConnectionId)) return;
        setStatus("error");
        setError(new Error("Shoutcast stream ended after drift reconnect"));
      };

      // Remove old listeners before adding new ones
      audioElement.removeEventListener("playing", onPlaying);
      audioElement.removeEventListener("timeupdate", onTimeupdate);
      audioElement.removeEventListener("error", onError);
      audioElement.removeEventListener("ended", onEnded);

      audioElement.addEventListener("playing", onPlaying);
      audioElement.addEventListener("timeupdate", onTimeupdate);
      audioElement.addEventListener("error", onError);
      audioElement.addEventListener("ended", onEnded);

      // Reconnect
      audioElement.pause();
      audioElement.src = shoutcastUrl;
      const playPromise = audioElement.play();
      if (playPromise) {
        playPromise.catch((err) => {
          if (isStaleConnection(newConnectionId)) return;
          if (err instanceof DOMException && err.name === "AbortError") return;
          diagnosticEventLog.record("shoutcast:audio", "error", "drift-reconnect-failed", {
            connectionId: newConnectionId,
            error: err.message,
          });
          const driftDiagnostics = getPlayerFailureDiagnostics(
            currentAddressRef.current ?? "unknown",
            "shoutcast",
            err.message ?? ""
          );
          Sentry.captureException(err, {
            tags: buildPlayerFailureTags(
              "useShoutcastPlayer",
              "shoutcast_drift_reconnect_failed",
              "shoutcast",
              driftDiagnostics
            ),
            extra: {
              connectionId: newConnectionId,
              address: currentAddressRef.current,
              driftSeconds,
              networkDiagnostics: driftDiagnostics.network,
            },
            fingerprint: ["useShoutcastPlayer", "drift-reconnect-failed"],
          });
          setStatus("error");
          setError(new Error(`Shoutcast drift reconnect play failed: ${err.message}`));
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [audioElement, isStaleConnection, stopLivenessMonitor, stopDriftMonitor]
  );

  // Start drift monitor — detects buffer drift and reconnects
  const startDriftMonitor = useCallback(
    (connectionId: number) => {
      if (driftIntervalRef.current) {
        clearInterval(driftIntervalRef.current);
      }

      driftIntervalRef.current = setInterval(() => {
        if (isStaleConnection(connectionId)) {
          clearInterval(driftIntervalRef.current!);
          driftIntervalRef.current = null;
          return;
        }

        if (!audioElement) return;

        try {
          const buffered = audioElement.buffered;
          if (buffered.length === 0) return;

          const end = buffered.end(buffered.length - 1);
          const drift = end - audioElement.currentTime;

          if (drift > DRIFT_THRESHOLD_S) {
            // Check cooldown
            if (Date.now() - lastDriftReconnectRef.current < DRIFT_RECONNECT_COOLDOWN_MS) {
              return;
            }

            diagnosticEventLog.record("shoutcast:audio", "warn", "drift-detected", {
              connectionId,
              driftSeconds: drift,
              address: currentAddressRef.current,
            });
            Sentry.addBreadcrumb({
              category: "audio",
              message: `Shoutcast drift detected (${drift.toFixed(1)}s)`,
              level: "warning",
              data: { connectionId, driftSeconds: drift, address: currentAddressRef.current },
            });
            console.warn(
              `[Shoutcast #${connectionId}] Drift detected: ${drift.toFixed(1)}s behind live edge — reconnecting`
            );

            performSilentReconnect(drift);
          }
        } catch (e) {
          // buffered may throw if element is in a bad state
          console.debug("[Shoutcast] Error reading buffered:", e);
        }
      }, DRIFT_CHECK_INTERVAL_MS) as unknown as number;
    },
    [audioElement, isStaleConnection, performSilentReconnect]
  );

  // Keep refs in sync for use by performSilentReconnect's onPlaying closure
  startLivenessMonitorRef.current = startLivenessMonitor;
  startDriftMonitorRef.current = startDriftMonitor;

  // Cleanup function
  const cleanup = useCallback(() => {
    stopLivenessMonitor();
    stopDriftMonitor();

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (audioElement) {
      try {
        audioElement.pause();
        audioElement.removeAttribute("src");
        audioElement.load();
      } catch (e) {
        console.debug("[Shoutcast] Error during cleanup:", e);
      }
    }

    currentAddressRef.current = null;
  }, [audioElement, stopLivenessMonitor, stopDriftMonitor]);

  // Connection effect
  useEffect(() => {
    if (!enabled || !authenticated || !address || !audioElement) {
      setStatus("idle");
      setError(null);
      setRetryCount(0);
      cleanup();
      return;
    }

    // Already connected to this address
    if (
      currentAddressRef.current === address &&
      (status === "connecting" || status === "connected")
    ) {
      return;
    }

    // Reset for new connection
    setStatus("idle");
    setError(null);
    setRetryCount(0);
    cleanup();

    currentAddressRef.current = address;
    connectionIdRef.current++;
    lastDriftReconnectRef.current = 0;
    const thisConnectionId = connectionIdRef.current;
    const shoutcastUrl = buildShoutcastUrl(address);

    console.log(`[Shoutcast #${thisConnectionId}] Connecting to ${shoutcastUrl}`);
    diagnosticEventLog.record("shoutcast:audio", "info", "connection-start", {
      connectionId: thisConnectionId,
      address,
    });
    setStatus("connecting");

    // Event handlers — all gated on connection ID
    const onPlaying = () => {
      if (isStaleConnection(thisConnectionId)) return;
      console.log(`[Shoutcast #${thisConnectionId}] Playing`);
      diagnosticEventLog.record("shoutcast:audio", "info", "connected", {
        connectionId: thisConnectionId,
      });
      setStatus("connected");
      setError(null);
      setRetryCount(0);
      startLivenessMonitor(thisConnectionId);
      startDriftMonitor(thisConnectionId);
    };

    const onTimeupdate = () => {
      if (isStaleConnection(thisConnectionId)) return;
      lastTimeupdateRef.current = Date.now();
    };

    const onError = () => {
      if (isStaleConnection(thisConnectionId)) return;
      const mediaError = audioElement.error;
      const errMsg = mediaError
        ? `Shoutcast MediaError code ${mediaError.code}: ${mediaError.message || "no message"}`
        : "Shoutcast stream error";
      console.error(`[Shoutcast #${thisConnectionId}] Error:`, errMsg);
      diagnosticEventLog.record("shoutcast:audio", "error", "stream-error", {
        connectionId: thisConnectionId,
        errorCode: mediaError?.code,
        errorMessage: mediaError?.message,
      });
      stopLivenessMonitor();
      stopDriftMonitor();
      setStatus("error");
      setError(new Error(errMsg));
    };

    const onEnded = () => {
      if (isStaleConnection(thisConnectionId)) return;
      console.warn(`[Shoutcast #${thisConnectionId}] Stream ended unexpectedly`);
      diagnosticEventLog.record("shoutcast:audio", "warn", "stream-ended", {
        connectionId: thisConnectionId,
      });
      stopLivenessMonitor();
      stopDriftMonitor();
      setStatus("error");
      setError(new Error("Shoutcast stream ended unexpectedly"));
    };

    const onStalled = () => {
      if (isStaleConnection(thisConnectionId)) return;
      console.warn(`[Shoutcast #${thisConnectionId}] Stream stalled (buffering)`);
      diagnosticEventLog.record("shoutcast:audio", "warn", "stalled", {
        connectionId: thisConnectionId,
      });
    };

    const onWaiting = () => {
      if (isStaleConnection(thisConnectionId)) return;
      console.log(`[Shoutcast #${thisConnectionId}] Waiting for data`);
    };

    // Register listeners
    audioElement.addEventListener("playing", onPlaying);
    audioElement.addEventListener("timeupdate", onTimeupdate);
    audioElement.addEventListener("error", onError);
    audioElement.addEventListener("ended", onEnded);
    audioElement.addEventListener("stalled", onStalled);
    audioElement.addEventListener("waiting", onWaiting);

    // Start playback
    audioElement.src = shoutcastUrl;
    const playPromise = audioElement.play();
    if (playPromise) {
      playPromise.catch((err) => {
        if (isStaleConnection(thisConnectionId)) return;
        if (err instanceof DOMException && err.name === "AbortError") {
          console.debug(`[Shoutcast #${thisConnectionId}] play() interrupted — expected during cleanup`);
          return;
        }
        console.error(`[Shoutcast #${thisConnectionId}] play() failed:`, err);
        diagnosticEventLog.record("shoutcast:audio", "error", "initial-play-failed", {
          connectionId: thisConnectionId,
          error: err.message,
        });
        const initialPlayDiagnostics = getPlayerFailureDiagnostics(
          address,
          "shoutcast",
          err.message ?? ""
        );
        Sentry.captureException(err, {
          tags: buildPlayerFailureTags(
            "useShoutcastPlayer",
            "shoutcast_initial_play_failed",
            "shoutcast",
            initialPlayDiagnostics
          ),
          extra: {
            connectionId: thisConnectionId,
            address,
            networkDiagnostics: initialPlayDiagnostics.network,
          },
          fingerprint: ["useShoutcastPlayer", "initial-play-failed"],
        });
        setStatus("error");
        setError(new Error(`Shoutcast play failed: ${err.message}`));
      });
    }

    // Cleanup on unmount or dependency change
    return () => {
      setStatus("disconnected");
      audioElement.removeEventListener("playing", onPlaying);
      audioElement.removeEventListener("timeupdate", onTimeupdate);
      audioElement.removeEventListener("error", onError);
      audioElement.removeEventListener("ended", onEnded);
      audioElement.removeEventListener("stalled", onStalled);
      audioElement.removeEventListener("waiting", onWaiting);
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, authenticated, address, audioElement, cleanup]);

  // Retry effect
  useEffect(() => {
    if (status !== "error" || !error || !enabled || !address || !audioElement) {
      return;
    }

    if (retryCount >= MAX_RETRIES) {
      console.error(`[Shoutcast] Max retries (${MAX_RETRIES}) reached, giving up`);
      diagnosticEventLog.record("shoutcast:audio", "error", "max-retries-reached", {
        retryCount,
      });
      const maxRetryDiagnostics = getPlayerFailureDiagnostics(
        address,
        "shoutcast",
        error?.message ?? ""
      );
      Sentry.captureException(
        new Error(`Shoutcast player failed after ${MAX_RETRIES} reconnection attempts`),
        {
          tags: buildPlayerFailureTags(
            "useShoutcastPlayer",
            "shoutcast_max_retries_exhausted",
            "shoutcast",
            maxRetryDiagnostics
          ),
          extra: {
            lastError: error?.message,
            address,
            retryCount,
            networkDiagnostics: maxRetryDiagnostics.network,
          },
          fingerprint: ["useShoutcastPlayer", "max-retries-exhausted"],
        }
      );
      return;
    }

    const delay = getRetryDelay(retryCount);
    console.log(
      `[Shoutcast] Retrying in ${(delay / 1000).toFixed(1)}s (attempt ${retryCount + 1}/${MAX_RETRIES})`
    );
    diagnosticEventLog.record("shoutcast:audio", "info", "retry-scheduled", {
      retryCount: retryCount + 1,
      maxRetries: MAX_RETRIES,
      delayMs: delay,
    });

    retryTimeoutRef.current = setTimeout(() => {
      if (!enabled || !address || !audioElement) return;

      setRetryCount((prev) => prev + 1);
      setStatus("connecting");
      setError(null);

      connectionIdRef.current++;
      const thisConnectionId = connectionIdRef.current;
      const shoutcastUrl = buildShoutcastUrl(address);

      console.log(`[Shoutcast Retry #${thisConnectionId}] Attempt ${retryCount + 1}/${MAX_RETRIES}`);

      // Re-register event listeners for this retry
      const onPlaying = () => {
        if (isStaleConnection(thisConnectionId)) return;
        console.log(`[Shoutcast #${thisConnectionId}] Playing (retry)`);
        diagnosticEventLog.record("shoutcast:audio", "info", "connected", {
          connectionId: thisConnectionId,
        });
        setStatus("connected");
        setError(null);
        startLivenessMonitor(thisConnectionId);
        startDriftMonitor(thisConnectionId);
      };

      const onTimeupdate = () => {
        if (isStaleConnection(thisConnectionId)) return;
        lastTimeupdateRef.current = Date.now();
      };

      const onError = () => {
        if (isStaleConnection(thisConnectionId)) return;
        const mediaError = audioElement.error;
        const errMsg = mediaError
          ? `Shoutcast MediaError code ${mediaError.code}: ${mediaError.message || "no message"}`
          : "Shoutcast stream error";
        console.error(`[Shoutcast #${thisConnectionId}] Error (retry):`, errMsg);
        stopLivenessMonitor();
        stopDriftMonitor();
        setStatus("error");
        setError(new Error(errMsg));
      };

      const onEnded = () => {
        if (isStaleConnection(thisConnectionId)) return;
        stopLivenessMonitor();
        stopDriftMonitor();
        setStatus("error");
        setError(new Error("Shoutcast stream ended unexpectedly"));
      };

      // Clean previous listeners and state
      audioElement.removeEventListener("playing", onPlaying);
      audioElement.removeEventListener("timeupdate", onTimeupdate);
      audioElement.removeEventListener("error", onError);
      audioElement.removeEventListener("ended", onEnded);

      audioElement.addEventListener("playing", onPlaying);
      audioElement.addEventListener("timeupdate", onTimeupdate);
      audioElement.addEventListener("error", onError);
      audioElement.addEventListener("ended", onEnded);

      try {
        audioElement.pause();
        audioElement.src = shoutcastUrl;
        const playPromise = audioElement.play();
        if (playPromise) {
          playPromise.catch((err) => {
            if (isStaleConnection(thisConnectionId)) return;
            if (err instanceof DOMException && err.name === "AbortError") return;
            diagnosticEventLog.record("shoutcast:audio", "error", "retry-play-failed", {
              connectionId: thisConnectionId,
              error: err.message,
            });
            const retryPlayDiagnostics = getPlayerFailureDiagnostics(
              address,
              "shoutcast",
              err.message ?? ""
            );
            Sentry.captureException(err, {
              tags: buildPlayerFailureTags(
                "useShoutcastPlayer",
                "shoutcast_retry_play_failed",
                "shoutcast",
                retryPlayDiagnostics
              ),
              extra: {
                connectionId: thisConnectionId,
                address,
                retryCount,
                networkDiagnostics: retryPlayDiagnostics.network,
              },
              fingerprint: ["useShoutcastPlayer", "retry-play-failed"],
            });
            setStatus("error");
            setError(new Error(`Shoutcast play failed: ${err.message}`));
          });
        }
      } catch (e) {
        setStatus("error");
        setError(new Error(`Shoutcast retry failed: ${(e as Error).message}`));
      }
    }, delay) as unknown as number;

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [
    status,
    error,
    retryCount,
    enabled,
    address,
    audioElement,
    isStaleConnection,
    startLivenessMonitor,
    stopLivenessMonitor,
    startDriftMonitor,
    stopDriftMonitor,
  ]);

  return {
    status,
    error,
    retryCount,
    setVolume,
  };
};
